import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FileProvider, useFileContext } from "context/FilesV2";
import {
  formatFiles,
  downloadFile,
  uploadFiles,
  deleteFile,
  deleteBundle,
} from "util";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FileGroup } from "common/FileUpload";
import { getFiles } from "apis/controllers/files";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { Attachment, Sync } from "@mui/icons-material";
import ReviewSection from "qubCommon/ReviewSection";
import { generateAttachments } from "apis/controllers/externalData";
import { createMarkup } from "util/createMarkup";

const FilesUpload = forwardRef(({ fileGroups, settings }, ref) => {
  const { fileGroups: fileGroupsV2, control: filesControl } = useFileContext();

  useImperativeHandle(ref, () => ({
    uploadFiles: async () => {
      try {
        const res = await filesControl.uploadFileGroups();
      } catch (e) {
        console.log(e);
      }
    },
    filebundles: {
      files: fileGroupsV2?.flatMap((group) => group?.bundles),
    },
  }));

  useEffect(() => {
    try {
      const formattedFiles = formatFiles(fileGroups);
      if (formattedFiles !== undefined) {
        filesControl.initialize(formattedFiles);
      } else {
        filesControl.initialize([]);
      }
    } catch (error) {
      console.error("error", error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileGroups]);
  return (
    <>
      {fileGroupsV2?.map((group, index) => (
        <FileGroup
          reviewMode={settings?.canApprove}
          isReadOnly={!settings?.canEdit}
          canAdd={settings?.canAdd}
          key={group.id}
          id={group.id}
          title={group?.title}
          maxSize={5242880}
          maxFiles={5}
          maxBundles={1}
          fields={group.fields}
          bundles={group?.bundles}
          templates={group?.templates}
          canViewReview={settings?.canViewReview}
        />
      ))}
    </>
  );
});

const ChildAttachments = ({ settings }, ref) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loadingWs, setLoadingWs] = useState(false);
  const [alert, setAlert] = useState({ severity: null, message: "" });
  const filesQuery = useQuery(
    ["application-files", id],
    async ({ queryKey }) => {
      const [, id] = queryKey;

      const getFilesRequest = {
        DataKey: "StudentApplication",
        DataValue: null,
        RecordId: null,
        RecordGuid: id,
        ProjectTableId: 6,
        applyRules: true,
      };
      const files = await getFiles(getFilesRequest);
      return files?.data;
    }
  );

  const AccordionTitle = () => (
    <Stack direction="row" gap={1}>
      <Attachment color="primary" fontSize="small" sx={{ marginLeft: "2px" }} />

      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΔΙΚΑΙΟΛΟΓΗΤΙΚΑ
      </Typography>
      {/* <Typography color="primary" sx={{ marginLeft: "18px", fontWeight: 500 }}>
          {institute?.abbr}
        </Typography> */}
    </Stack>
  );

  const handleWs = async () => {
    setLoadingWs(true)
    try {
      const res = await generateAttachments(id)
      setAlert({ severity: res?.status, message: res?.message })
    }
    catch {
      setAlert({ severity: 'error', message: 'Παρουσιάστηκε σφάλμα κατά την επικοινωνία με την υπηρεσία. Παρακαλούμε δοκιμάστε ξανά αργότερα.' })
    }
    finally {
      filesQuery.refetch()
      setLoadingWs(false)
    }
  }

  return {
    loading: <Skeleton />,
    success: !filesQuery?.data ? null : (
      <CustomSimpleAccordion
        title={<AccordionTitle />}
        content={
          <FileProvider
            downloadFile={(fileId, type) => downloadFile(fileId, type)}
            uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
            onDeleteBundle={async (bundleId) =>
              await deleteBundle(
                bundleId,
                6,
                null,
                id === "0" ? null : id,
                null
              )
            }
            onDeleteFile={async (fileId) =>
              await deleteFile(fileId, 6, null, id === "0" ? null : id, null)
            }
          >
            {settings?.wsBtnEnabled && (
              <Button
                sx={{ mb: 1 }}
                variant="contained"
                disabled={(filesQuery.isFetching || loadingWs)}
                startIcon={
                  (filesQuery.isFetching || loadingWs) ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <Sync />
                  )
                }
                onClick={handleWs}
              >
                WebService - Πιστοποιητικό Οικογενειακής Κατάστασης
              </Button>
            )}
            <FilesUpload
              fileGroups={filesQuery?.data}
              ref={ref}
              settings={settings}
            />
            <Dialog
              open={Boolean(alert?.severity && alert?.message)}
              onClose={() => setAlert({ severity: "", message: "" })}
            >
              <DialogTitle variant='h4'>
                {alert?.severity === "success"
                  ? t("Επιτυχία")
                  : alert?.severity === "error"
                    ? t("Σφάλμα")
                    : t("Ενημέρωση")}
              </DialogTitle>
              <DialogContent dividers>
                <div dangerouslySetInnerHTML={createMarkup(alert?.message)} />
              </DialogContent>
              <DialogActions>
                <Button variant='contained' onClick={() => setAlert({ severity: "", message: "" })}>
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          </FileProvider>
        }
      />
    ),
    error: <>Error fetching required attachments</>,
  }[filesQuery?.status];
};

export default forwardRef(ChildAttachments);
